import React, { useEffect, useState } from 'react';

import { getOrganizationActivity } from '@/services/Organization';

interface RecentSpendingActivityProps {
  onViewAll?: () => void;
  activities?: any[];
}

const getInitials = (name: string) => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase() || 'EM';
};

const getAvatarBg = (name: string) => {
  const colors = ['#2D3748', '#4A5568', '#3B82F6', '#6366F1', '#8B5CF6', '#10B981', '#F59E0B'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const formatSpendingTitle = (item: any) => {
  if (item.member?.user?.name) return item.member.user.name;
  if (item.member?.invited_name) return item.member.invited_name;
  if (item.user_name) return item.user_name;
  if (item.name) return item.name;
  if (item.transaction_type === 'product_purchase') return 'Product Purchase';
  if (item.transaction_type === 'wallet_recharge') return 'Wallet Top-up';
  if (item.type === 'credit') return 'Wallet Credit';
  return 'Team Member';
};

const formatSpendingDescription = (item: any) => {
  if (item.order?.order_details?.[0]?.product?.name) {
    return item.order.order_details[0].product.name;
  }
  if (item.order?.order_details?.[0]?.name) {
    return item.order.order_details[0].name;
  }
  if (item.description) return item.description;
  if (item.balance_after) {
    return `Balance: ₹${(Number(item.balance_after) || 0).toLocaleString('en-IN')}`;
  }
  if (item.books_count) {
    return `${item.books_count} ${item.books_count === 1 ? 'Book' : 'Books'} bought`;
  }
  return 'Learning Playbook';
};

const formatSpendingTime = (item: any) => {
  if (item.time_ago) return item.time_ago;
  if (item.created_at) {
    try {
      const d = new Date(item.created_at);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    } catch (err) {
      // Ignore date formatting fallback
    }
    return item.created_at;
  }
  return 'Recent';
};

const RecentSpendingActivity: React.FC<RecentSpendingActivityProps> = ({
  onViewAll,
  activities,
}) => {
  const [activityList, setActivityList] = useState<any[]>(activities || []);
  const [loading, setLoading] = useState(!activities || activities.length === 0);

  useEffect(() => {
    if (activities && activities.length > 0) {
      setActivityList(activities);
      setLoading(false);
    } else {
      setLoading(true);
      getOrganizationActivity(1)
        .then((res) => {
          if (Array.isArray(res)) {
            setActivityList(res.slice(0, 5));
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [activities]);

  return (
    <div className='bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-6 shadow-sm'>
      {/* Title */}
      <h2 className='text-base font-bold text-white tracking-tight mb-4'>
        Recent Spending Activity
      </h2>

      {/* Loading Skeleton */}
      {loading ? (
        <div className='divide-y divide-[#1A1A1A]'>
          {[1, 2, 3].map((i) => (
            <div key={i} className='py-3.5 flex items-center justify-between animate-pulse'>
              <div className='flex items-center gap-3.5'>
                <div className='w-9 h-9 rounded-full bg-[#1C1C1C]' />
                <div className='space-y-1.5'>
                  <div className='h-3.5 bg-[#1C1C1C] rounded w-28' />
                  <div className='h-2.5 bg-[#1C1C1C] rounded w-44' />
                </div>
              </div>
              <div className='space-y-1.5 text-right'>
                <div className='h-2.5 bg-[#1C1C1C] rounded w-12 ml-auto' />
                <div className='h-3.5 bg-[#1C1C1C] rounded w-16 ml-auto' />
              </div>
            </div>
          ))}
        </div>
      ) : activityList.length === 0 ? (
        <p className='text-xs text-grayZl-500 py-6 text-center'>
          No recent spending activity found.
        </p>
      ) : (
        <div className='divide-y divide-[#1A1A1A]'>
          {activityList.map((item, idx) => {
            const userName = formatSpendingTitle(item);
            const desc = formatSpendingDescription(item);
            const time = formatSpendingTime(item);
            const rawAmount = item.amount ?? item.inr_amount ?? 0;
            const amount = Number(rawAmount) || 0;
            const isCredit = item.type === 'credit' || item.transaction_type === 'wallet_recharge';

            return (
              <div
                key={item.id || idx}
                className='py-3.5 flex items-center justify-between first:pt-0 last:pb-3 hover:bg-[#121212] px-2 -mx-2 rounded-lg transition-colors'
              >
                {/* User details */}
                <div className='flex items-center gap-3.5'>
                  <div
                    className='w-[38px] h-[38px] min-w-[38px] min-h-[38px] max-w-[38px] max-h-[38px] rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0 select-none shadow-sm'
                    style={{
                      backgroundColor: getAvatarBg(userName),
                    }}
                  >
                    {getInitials(userName)}
                  </div>
                  <div>
                    <p className='text-sm font-semibold text-white leading-tight'>
                      {userName}
                    </p>
                    <p className='text-xs text-grayZl-400 mt-0.5'>{desc}</p>
                  </div>
                </div>

                {/* Time & Price */}
                <div className='text-right'>
                  <span className='text-xs text-grayZl-400 block mb-0.5'>
                    {time}
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      isCredit ? 'text-[#4ADE80]' : 'text-[#FF5612]'
                    }`}
                  >
                    {isCredit ? '+' : '-'}₹{amount.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View All Footer */}
      {!loading && activityList.length > 0 && (
        <div className='pt-3 border-t border-[#1E1E1E] text-center'>
          <button
            type='button'
            onClick={onViewAll}
            className='text-xs font-bold text-[#FF5612] hover:text-[#E54D10] transition-colors hover:underline cursor-pointer py-1'
          >
            View All Activity
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentSpendingActivity;
